import { useState, useEffect, useCallback } from "react";
import { adminAxios } from "../../lib/admin";

const fmt = (iso) => new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });

const StatCard = ({ label, value, testid }) => (
  <div data-testid={testid} className="border border-onix-line bg-onix-black2 p-6">
    <p className="tech-label mb-3">{label}</p>
    <p className="font-serif font-bold text-3xl md:text-4xl text-onix-white">{value}</p>
  </div>
);

const TopList = ({ title, items, testid }) => (
  <div data-testid={testid} className="border border-onix-line bg-onix-black2 p-6">
    <p className="eyebrow mb-5">{title}</p>
    {items.length === 0 && <p className="text-xs text-onix-gray">Sin datos todavía.</p>}
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={i} className="flex items-center gap-3 text-sm">
          <span className="text-onix-silver flex-1 truncate" title={it.label}>
            {it.label}
          </span>
          <span className="font-bold text-onix-white">{it.count}</span>
          <span
            className="h-1.5 meter-bar"
            style={{ width: `${Math.max(6, (it.count / (items[0]?.count || 1)) * 60)}px` }}
            aria-hidden="true"
          />
        </li>
      ))}
    </ul>
  </div>
);

export default function AdminAnalytics({ onNewMessages }) {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setError("");
    adminAxios
      .get(`/admin/stats?days=${days}`)
      .then(({ data }) => {
        setStats(data);
        onNewMessages(data.messages.nuevas);
      })
      .catch(() => setError("No se pudo cargar la analítica."));
  }, [days, onNewMessages]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) {
    return (
      <p data-testid="admin-stats-error" role="alert" className="text-sm text-onix-silver border border-dashed border-onix-silver p-5">
        {error}
      </p>
    );
  }
  if (!stats) return <p className="tech-label">CARGANDO ANALÍTICA…</p>;

  const maxDay = Math.max(1, ...stats.day_series.map((d) => d.count));

  return (
    <div data-testid="admin-analytics" className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-serif font-bold text-2xl text-onix-white">Analítica del sitio</h2>
        <div className="flex gap-2" role="group" aria-label="Rango de días">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              data-testid={`admin-days-${d}`}
              onClick={() => setDays(d)}
              className={`px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] border transition-colors ${
                days === d ? "border-onix-white text-onix-black bg-onix-white" : "border-onix-line text-onix-silver hover:border-onix-silver"
              }`}
            >
              {d} días
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard testid="admin-stat-visits" label="VISITAS" value={stats.total_visits} />
        <StatCard testid="admin-stat-unique" label="VISITANTES ÚNICOS (IP)" value={stats.unique_visitors} />
        <StatCard testid="admin-stat-sessions" label="SESIONES" value={stats.unique_sessions} />
        <StatCard testid="admin-stat-today" label="VISITAS HOY" value={stats.visits_today} />
        <StatCard testid="admin-stat-messages" label="MENSAJES (NUEVOS)" value={`${stats.messages.total} (${stats.messages.nuevas})`} />
      </div>

      <div data-testid="admin-day-chart" className="border border-onix-line bg-onix-black2 p-6">
        <p className="eyebrow mb-6">Visitas por día · últimos {stats.days} días</p>
        <div className="flex items-end gap-[2px] h-40">
          {stats.day_series.map((d) => (
            <div
              key={d.date}
              className="flex-1 chart-bar transition-colors min-w-[2px]"
              style={{ height: `${Math.max(2, (d.count / maxDay) * 100)}%` }}
              title={`${d.date}: ${d.count} visitas`}
            />
          ))}
        </div>
        <div className="flex justify-between mt-3">
          <span className="tech-label">{stats.day_series[0]?.date}</span>
          <span className="tech-label">{stats.day_series[stats.day_series.length - 1]?.date}</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        <TopList testid="admin-top-pages" title="Páginas más vistas" items={stats.top_pages} />
        <TopList testid="admin-top-countries" title="Países" items={stats.top_countries} />
        <TopList testid="admin-top-cities" title="Ciudades" items={stats.top_cities} />
        <TopList testid="admin-top-referrers" title="Procedencia (referrer)" items={stats.top_referrers} />
        <TopList testid="admin-devices" title="Dispositivos" items={stats.devices} />
        <TopList testid="admin-languages" title="Idiomas" items={stats.languages} />
      </div>

      <div className="border border-onix-line bg-onix-black2 p-6 overflow-x-auto">
        <p className="eyebrow mb-5">Últimas visitas</p>
        <table data-testid="admin-recent-table" className="w-full text-left text-xs md:text-sm min-w-[820px]">
          <thead>
            <tr className="border-b border-onix-line">
              {["Fecha y hora", "IP", "Ciudad", "País", "Página", "Dispositivo", "Idioma"].map((h) => (
                <th key={h} className="py-2.5 pr-4 text-[10px] uppercase tracking-[0.12em] text-onix-gray font-bold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {stats.recent.length === 0 && (
              <tr>
                <td colSpan={7} className="py-4 text-onix-gray">
                  Sin visitas registradas todavía. La analítica se activa cuando el visitante acepta las cookies analíticas.
                </td>
              </tr>
            )}
            {stats.recent.map((v) => (
              <tr key={v.id} className="border-b border-onix-line">
                <td className="py-2.5 pr-4 text-onix-silver whitespace-nowrap">{fmt(v.ts)}</td>
                <td className="py-2.5 pr-4 text-onix-white font-bold">{v.ip}</td>
                <td className="py-2.5 pr-4 text-onix-silver">{v.city || "—"}</td>
                <td className="py-2.5 pr-4 text-onix-silver">{v.country || "—"}</td>
                <td className="py-2.5 pr-4 text-onix-silver truncate max-w-[180px]" title={v.path}>
                  {v.path}
                </td>
                <td className="py-2.5 pr-4 text-onix-silver">{v.device}</td>
                <td className="py-2.5 pr-4 text-onix-silver uppercase">{v.lang || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
